<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { holidaysApi } from '$lib/api/holidays';
  import { t } from '$lib/i18n';
  import type { Holiday } from '$lib/types';

  const query = createQuery(() => ({
    queryKey: ['holidays'],
    queryFn: () => holidaysApi.list()
  }));

  function sorted(list: Holiday[]): Holiday[] {
    return [...list].sort((a, b) => a.date.localeCompare(b.date));
  }

  function fmt(date: string): string {
    return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  function isUpcoming(date: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(`${date}T00:00:00`) >= today;
  }
</script>

{#snippet holidayList(list: Holiday[])}
  <ul class="divide-y divide-[#f0f0f0]">
    {#each sorted(list) as h (h.date + h.name)}
      <li class="flex items-center justify-between px-5 py-3 text-[13.5px]">
        <span class="text-toyama-heavy-carbon">{h.name}</span>
        <span class="flex items-center gap-2">
          {#if isUpcoming(h.date)}
            <span class="tym-badge" style="background:#ecf8cc;color:#5b7a00">{t('holidays.upcoming')}</span>
          {/if}
          <span class="muted-text">{fmt(h.date)}</span>
        </span>
      </li>
    {:else}
      <li class="empty px-5 py-8 text-center">{t('holidays.empty')}</li>
    {/each}
  </ul>
{/snippet}

<div class="tym-page">
  <p class="tym-section-sub mb-4">{t('holidays.subtitle')}</p>

  {#if query.isLoading}
    <div class="tym-loading-state">{t('common.loading')}</div>
  {:else if query.isError}
    <div class="tym-error-state">{t('holidays.error')}</div>
  {:else if query.data}
    <div class="mb-5">
      <p class="mb-2 text-[11px] font-semibold uppercase tracking-[.06em] text-dark-gray">{t('holidays.national')}</p>
      <div class="tym-table-wrap">
        {@render holidayList(query.data.national_holidays_japan)}
      </div>
    </div>
    <div>
      <p class="mb-2 text-[11px] font-semibold uppercase tracking-[.06em] text-dark-gray">{t('holidays.company')}</p>
      <div class="tym-table-wrap">
        {@render holidayList(query.data.company_holidays)}
      </div>
    </div>
  {/if}
</div>

<style>
  .muted-text {
    color: var(--tym-dark-gray);
  }
</style>
